import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, FileText, Sparkles, Trophy, Unlock, MapPin, Loader2, Plus,
} from "lucide-react";

function Metric({ label, value, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="rounded-lg border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-500">{label}</span>
        <Icon className="h-4 w-4 text-slate-400" />
      </div>
      <div className="mt-3 font-heading text-3xl font-semibold tracking-tight">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => setStats({ total_reports: 0, recent: [] }));
  }, []);

  const firstName = (user?.name || "there").split(" ")[0];

  return (
    <div className="mx-auto max-w-6xl px-6 py-10 animate-fade-up">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">Welcome back, {firstName}</h1>
          <p className="mt-1 text-slate-500">Here's your relocation overview.</p>
        </div>
        <Button data-testid="start-assessment-btn" onClick={() => navigate("/assessment")} className="h-11 gap-2 font-medium">
          <Sparkles className="h-4 w-4" /> Start new assessment
        </Button>
      </div>

      {!stats ? (
        <div className="mt-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>
      ) : (
        <>
          <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Metric testid="metric-total" label="Reports" value={stats.total_reports} icon={FileText} />
            <Metric testid="metric-best" label="Best match" value={stats.best_match_score ? `${stats.best_match_score}%` : "—"} icon={Trophy} />
            <Metric testid="metric-unlocked" label="Unlocked" value={stats.unlocked_reports || 0} icon={Unlock} />
            <Metric testid="metric-topcity" label="Top city" value={stats.latest_top_city || "—"} icon={MapPin} />
          </div>

          <div className="mt-10">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-semibold tracking-tight">Recent reports</h2>
              <Button variant="ghost" size="sm" onClick={() => navigate("/reports")} data-testid="view-all-reports">
                View all <ArrowRight className="ml-1 h-3.5 w-3.5" />
              </Button>
            </div>

            {stats.recent?.length ? (
              <div className="mt-4 divide-y divide-slate-200 rounded-lg border border-slate-200">
                {stats.recent.map((r) => (
                  <button
                    key={r.id}
                    data-testid={`recent-report-${r.id}`}
                    onClick={() => navigate(`/results/${r.id}`)}
                    className="flex w-full items-center justify-between px-5 py-4 text-left transition-colors hover:bg-slate-50"
                  >
                    <div className="min-w-0">
                      <div className="truncate font-medium">{r.top_cities?.[0]?.name}, {r.top_cities?.[0]?.country}</div>
                      <div className="truncate text-sm text-slate-500">{r.title}</div>
                    </div>
                    <div className="flex items-center gap-4 pl-4">
                      <span className="font-heading text-lg font-semibold">{r.overall_match_score}%</span>
                      <ArrowRight className="h-4 w-4 text-slate-400" />
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="mt-4 rounded-lg border border-dashed border-slate-300 bg-dotted py-16 text-center">
                <p className="font-medium">No reports yet</p>
                <p className="mt-1 text-sm text-slate-500">Take your first assessment to discover your best-matched cities.</p>
                <Button onClick={() => navigate("/assessment")} className="mt-5 gap-2" data-testid="empty-start-btn">
                  <Plus className="h-4 w-4" /> Take assessment
                </Button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
